import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { type AvatarTone, ProductMark, toneInk } from "./Avatar";
import { ForwardIcon, StarIcon } from "./icons";
import { type DeliveryStatus, deliveryLabel, Ticks } from "./Ticks";
import { cn, focusRing } from "./utils";

export type Reaction = { emoji: string; count: number; label?: string; mine?: boolean };

export type MessageBubbleProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  direction: "in" | "out";
  time: string;
  /** Delivery of a message you sent. */
  status?: DeliveryStatus;
  /** Spoken label for the ticks, such as "Read by everyone". */
  statusLabel?: string;
  edited?: boolean;
  /** A short mark before the time, such as "Imported from WhatsApp". */
  note?: string;
  /** Sender name in group chats. */
  author?: { name: string; tone?: AvatarTone };
  reply?: { author: string; text: string };
  reactions?: Reaction[];
  /** A photo or video shown above the text, edge to edge. */
  media?: ReactNode;
  /** A card inside the bubble: link preview, file, poll or location. */
  attachment?: ReactNode;
  /** Queravel writes on a green header so AI text is always marked as such. */
  variant?: "default" | "queravel" | "failed";
  onRetry?: () => void;
  /** Children draw their own padding and time, as voice messages do. */
  bare?: boolean;
  hideMeta?: boolean;
  width?: number;
  /** Lifted above a scrim, as in the long-press menu. */
  elevated?: boolean;
  /** Sent on from another chat with Forward. */
  forwarded?: boolean;
  /** You starred it. A small star shows beside the time. */
  starred?: boolean;
  /** The message a link opened, picked out for a moment. */
  highlighted?: boolean;
  /**
   * Desktop: a small bar of actions that shows while the pointer is over the message or focus is
   * inside it, as in Slack.
   */
  toolbar?: ReactNode;
  children?: ReactNode;
};

function Meta({
  direction,
  time,
  status,
  statusLabel,
  edited,
  note,
  starred,
  float,
}: Pick<
  MessageBubbleProps,
  "direction" | "time" | "status" | "statusLabel" | "edited" | "note" | "starred"
> & {
  float: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-[3px] text-[11px] leading-none",
        direction === "out" ? "text-sent-meta" : "text-ink-meta",
        float ? "float-right mt-[9px] ml-2.5" : "flex justify-end px-1 pt-1",
      )}
    >
      {starred && <StarIcon size={11} strokeWidth={2.4} label="Starred" className="fill-current" />}
      {note && <span>{note} ·</span>}
      {edited && <span>edited ·</span>}
      <span>{time}</span>
      {direction === "out" && status && <Ticks status={status} label={statusLabel} />}
    </span>
  );
}

export function ReactionPill({ reactions, className }: { reactions: Reaction[]; className?: string }) {
  const spoken = reactions.map((r) => `${r.label ?? r.emoji} ${r.count}`).join(", ");
  return (
    <span
      role="img"
      aria-label={`Reactions: ${spoken}`}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border-2 border-chat bg-surface px-2 py-[3px] text-xs font-semibold text-ink",
        className,
      )}
    >
      {reactions.map((r) => (
        <span key={r.emoji} className="inline-flex items-center gap-1">
          <span aria-hidden="true">{r.emoji}</span>
          {r.count > 1 && <span aria-hidden="true">{r.count}</span>}
        </span>
      ))}
    </span>
  );
}

export function MessageBubble({
  direction,
  time,
  status,
  statusLabel,
  edited = false,
  note,
  author,
  reply,
  reactions,
  media,
  attachment,
  variant = "default",
  onRetry,
  bare = false,
  hideMeta = false,
  width,
  elevated = false,
  forwarded = false,
  starred = false,
  highlighted = false,
  toolbar,
  className,
  style,
  children,
  ...rest
}: MessageBubbleProps) {
  const out = direction === "out";
  const shape = out ? "rounded-[18px_18px_6px_18px]" : "rounded-[18px_18px_18px_6px]";
  const bubbleStyle: CSSProperties = { ...style, ...(width ? { width } : {}) };

  if (variant === "failed") {
    return (
      <div
        className={cn("max-w-[78%] min-[900px]:max-w-[62%]", out ? "self-end" : "self-start", className)}
        style={bubbleStyle}
        {...rest}
      >
        <div
          className={cn(
            shape,
            "whitespace-pre-wrap bg-scarlet-tint px-3.5 pt-2.5 pb-2 text-[15px] leading-[1.45] text-ink [overflow-wrap:anywhere]",
          )}
        >
          {children}
        </div>
        <button
          type="button"
          onClick={onRetry}
          className={cn("mt-1 cursor-pointer text-[13px] font-semibold text-scarlet", focusRing)}
        >
          Not sent. Tap to retry.
        </button>
      </div>
    );
  }

  const hasBlocks = Boolean(reply || media || attachment);
  const queravel = variant === "queravel";
  const showFloatMeta = !hideMeta && !bare && children !== undefined;
  const showRowMeta = !hideMeta && !bare && children === undefined;

  return (
    <div
      className={cn(
        "group/message relative max-w-[78%] min-[900px]:max-w-[62%]",
        out ? "self-end bg-blue-container" : "self-start bg-surface",
        highlighted && "ring-[3px] ring-blue/45 ring-offset-2 ring-offset-chat transition-shadow",
        elevated ? "shadow-lift" : !out && variant !== "queravel" && "shadow-bubble",
        shape,
        queravel && "overflow-hidden border border-hairline",
        bare ? "" : hasBlocks ? (media ? "p-1" : "p-[7px]") : queravel ? "" : "px-[13px] pt-[9px] pb-[7px]",
        reactions?.length ? "mb-3.5" : "",
        "text-[15px] leading-[1.45] text-ink",
        className,
      )}
      style={bubbleStyle}
      {...rest}
    >
      {queravel && (
        <div className="flex items-center gap-2 bg-queravel-tint px-3 py-[9px]">
          <ProductMark product="queravel" size={22} />
          <span className="flex-1 text-[13px] font-bold text-queravel">Suggested by Queravel</span>
        </div>
      )}
      {author && (
        <div
          className={cn(
            "text-[12.5px] font-bold",
            toneInk[author.tone ?? "stone"],
            hasBlocks && "px-[5px] pb-[5px]",
            queravel && "px-3 pt-2",
          )}
        >
          {author.name}
        </div>
      )}
      {forwarded && (
        <div
          className={cn(
            "mb-0.5 flex items-center gap-1 text-[12px] text-ink-meta italic",
            hasBlocks && "px-[5px] pt-0.5",
            queravel && "px-3 pt-2",
          )}
        >
          <ForwardIcon size={12} strokeWidth={2.2} />
          Forwarded
        </div>
      )}
      {reply && (
        <div className="mb-1.5 rounded-[10px] bg-chat px-2.5 py-[7px]">
          <div className="text-[12.5px] font-bold text-blue">{reply.author}</div>
          <div className="truncate text-[13.5px] text-ink-secondary">{reply.text}</div>
        </div>
      )}
      {media && <div className="overflow-hidden rounded-[14px]">{media}</div>}
      {attachment}
      {children !== undefined && (
        <div
          className={cn(
            // Line breaks show as typed, and a long word or link wraps inside the bubble.
            "select-text whitespace-pre-wrap [overflow-wrap:anywhere]",
            bare ? "" : hasBlocks ? (media ? "px-[9px] pt-[7px] pb-[3px]" : "px-1.5 pt-1") : "",
            queravel && "px-3 py-2.5 font-serif",
          )}
        >
          {children}
          {showFloatMeta && (
            <Meta
              direction={direction}
              time={time}
              status={status}
              statusLabel={statusLabel}
              edited={edited}
              note={note}
              starred={starred}
              float
            />
          )}
        </div>
      )}
      {showRowMeta && (
        <Meta
          direction={direction}
          time={time}
          status={status}
          statusLabel={statusLabel}
          edited={edited}
          note={note}
          starred={starred}
          float={false}
        />
      )}
      {reactions && reactions.length > 0 && (
        <ReactionPill reactions={reactions} className="absolute bottom-[-14px] left-3" />
      )}
      {out && status && hideMeta && <span className="sr-only">{statusLabel ?? deliveryLabel(status)}</span>}
      {toolbar && (
        // Beside the message, on the side away from the edge. The padding keeps the pointer over the
        // message while it moves across to the bar.
        <div
          className={cn(
            "pointer-events-none absolute top-0 z-10 flex opacity-0 transition-opacity",
            out ? "right-full pr-1.5" : "left-full pl-1.5",
            "group-hover/message:pointer-events-auto group-hover/message:opacity-100",
            "group-focus-within/message:pointer-events-auto group-focus-within/message:opacity-100",
          )}
        >
          {toolbar}
        </div>
      )}
    </div>
  );
}

// An incoming message in a group, with the sender's avatar beside it.
export function GroupedMessage({ avatar, children }: { avatar?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-end gap-2 self-stretch">
      <span className="flex w-7 flex-none justify-center">{avatar}</span>
      <div className="flex min-w-0 flex-1 flex-col items-start">{children}</div>
    </div>
  );
}

export function TypingBubble({ name }: { name: string }) {
  return (
    <div
      role="status"
      aria-label={`${name} is typing`}
      className="flex items-center gap-[5px] self-start rounded-[18px_18px_18px_6px] bg-surface px-4 py-3.5"
    >
      <span className="h-2 w-2 animate-pulse rounded-full bg-blue" />
      <span className="h-2 w-2 animate-pulse rounded-full bg-typing-2 [animation-delay:150ms]" />
      <span className="h-2 w-2 animate-pulse rounded-full bg-typing-3 [animation-delay:300ms]" />
    </div>
  );
}
